<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useStoreGetters } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useAdmin } from 'dashboard/composables/useAdmin';
import Button from 'dashboard/components-next/button/Button.vue';
import SettingsLayout from '../../SettingsLayout.vue';
import BaseSettingsHeader from '../../components/BaseSettingsHeader.vue';
import MessageTemplateTabs from '../MessageTemplateTabs.vue';
import TemplateRow from './TemplateRow.vue';
import TemplateForm from './TemplateForm.vue';

const store = useStore();
const getters = useStoreGetters();
const { t } = useI18n();
const { isAdmin } = useAdmin();

const selectedInboxId = ref(null);
const formRef = ref(null);

// Só Cloud API tem template: o getter cobre o canal WhatsApp, e o provider
// separa das inboxes 360dialog.
const cloudInboxes = computed(() =>
  (getters['inboxes/getWhatsAppInboxes'].value || []).filter(
    inbox => inbox.provider === 'whatsapp_cloud'
  )
);

const selectedInbox = computed(
  () =>
    cloudInboxes.value.find(inbox => inbox.id === selectedInboxId.value) ||
    cloudInboxes.value[0]
);

const templates = computed(() => selectedInbox.value?.message_templates || []);
const uiFlags = computed(() => getters['whatsappTemplates/getUIFlags'].value);

onMounted(() => {
  store.dispatch('inboxes/get');
});

const sync = async () => {
  await store.dispatch('whatsappTemplates/sync', selectedInbox.value.id);
  useAlert(t('CANNED_MGMT.WHATSAPP_TEMPLATES.SYNC_STARTED'));
};
</script>

<template>
  <SettingsLayout
    :no-records-found="!cloudInboxes.length"
    :no-records-message="$t('CANNED_MGMT.WHATSAPP_TEMPLATES.EMPTY_INBOXES')"
  >
    <template #header>
      <BaseSettingsHeader
        :title="$t('CANNED_MGMT.WHATSAPP_TEMPLATES.HEADER')"
        :description="$t('CANNED_MGMT.WHATSAPP_TEMPLATES.DESCRIPTION')"
        feature-name="canned_responses"
      >
        <template #actions>
          <Button
            v-if="isAdmin && selectedInbox"
            :label="$t('CANNED_MGMT.WHATSAPP_TEMPLATES.NEW')"
            size="sm"
            @click="formRef?.open()"
          />
        </template>
      </BaseSettingsHeader>
      <MessageTemplateTabs />
    </template>
    <template #body>
      <div v-if="cloudInboxes.length" class="flex flex-col gap-3">
        <div class="flex items-center justify-between gap-2">
          <select v-model="selectedInboxId" class="mb-0">
            <option
              v-for="inbox in cloudInboxes"
              :key="inbox.id"
              :value="inbox.id"
            >
              {{ inbox.name }}
            </option>
          </select>
          <Button
            faded
            slate
            sm
            :label="$t('CANNED_MGMT.WHATSAPP_TEMPLATES.SYNC')"
            :is-loading="uiFlags.isSyncing"
            @click="sync"
          />
        </div>

        <p v-if="!templates.length" class="text-sm text-n-slate-11">
          {{ $t('CANNED_MGMT.WHATSAPP_TEMPLATES.EMPTY_TEMPLATES') }}
        </p>
        <TemplateRow
          v-for="template in templates"
          :key="`${template.name}-${template.language}`"
          :template="template"
        />

        <TemplateForm
          v-if="selectedInbox"
          ref="formRef"
          :inbox="selectedInbox"
        />
      </div>
    </template>
  </SettingsLayout>
</template>
